import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit, Renderer2 } from '@angular/core';
import { MatIconRegistry, SafeResourceUrlWithIconOptions } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService, rateNowCtlTheme } from '@nx-ratenow/core/config';
import { LayoutService, SplashScreenService } from '@nx-ratenow/core/data-access-layaout';
import { NavigationServiceCTL, NavigationStateCTL } from '@nx-ratenow/core/data-access-navigation-ctl';
import { NavigationStateImports } from '@nx-ratenow/core/data-access-navigation-imports';
import { RbacService } from '@nx-ratenow/core/data-access-role-access';
import { UserFacade } from '@nx-ratenow/core/data-access-user';
import { environment } from '@nx-ratenow/core/environment';
import { ToasterService } from '@nx-ratenow/core/feature-notifications';
import { NotificationsFacade } from '@nx-ratenow/core/layout/feature-toolbar';
import { BasicQuestionnaireDto } from '@nx-ratenow/shared/models';
import { Settings } from 'luxon';
import { combineLatest, switchMap, timer, withLatestFrom } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nx-ratenow-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  lastRouterEvent: any;
  lastNavigationObject: NavigationStateImports;

  constructor(
    private configService: ConfigService,
    private renderer: Renderer2,
    private platform: Platform,
    @Inject(DOCUMENT) private document: Document,
    @Inject(LOCALE_ID) private localeId: string,
    private layoutService: LayoutService,
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationServiceCTL,
    private splashScreenService: SplashScreenService,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly translate: TranslateService,
    private http: HttpClient,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef,
    private userFacade: UserFacade,
    private notificationFacade: NotificationsFacade,
    private rbacService: RbacService
  ) {
    // SETTING CONFIG
    Settings.defaultLocale = this.localeId;

    this.configService.updateConfig(rateNowCtlTheme);

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }

    // TODO: Move this into project.json or a service
    this.matIconRegistry.addSvgIconResolver((name: string, namespace: string): SafeResourceUrl | SafeResourceUrlWithIconOptions | null => {
      switch (namespace) {
        case 'mat':
          return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/material-design-icons/two-tone/${name}.svg`);
        case 'logo':
          return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/logos/${name}.svg`);
        case 'flag':
          return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/flags/${name}.svg`);
        default:
          return null;
      }
    });

    this.navigationService.loadNavigation();

    this.userFacade.userLogged$
      .pipe(
        filter((val) => val != undefined),
        switchMap((userLogged) => {
          this.rbacService.setAuthorities(userLogged.authorities);
          return this.http.get(`${environment.serverBackend}/questionnaire/service/CTL`, {
            params: {
              customerId: userLogged.customerId
            }
          });
        }),
        withLatestFrom(this.navigationService.getCurrentNavigation())
      )
      .subscribe(([listOfQuestionnaires, currentNav]: [BasicQuestionnaireDto[], NavigationStateCTL]) => {
        if (listOfQuestionnaires.length === 0 && !rbacService.isGranted('questionnaires_view')) {
          this.userFacade.logOut();
          this.toasterService.warning('El usuario al que tratas de acceder no poseé cuestionarios activos para el CTL');
          return;
        }

        this.navigationService.setInitialNavigation(listOfQuestionnaires, currentNav);
      });
  }

  ngOnInit(): void {
    // SETTING LANGUAGES

    this.document.documentElement.lang = this.translate.currentLang;
    this.translate.onLangChange.subscribe((lang) => (this.document.documentElement.lang = lang.lang));

    // NAVIGATION DYNAMIC SETTING ROUTE

    combineLatest([
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        filter((router) => (router as NavigationEnd).url === '/'),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      ),
      this.navigationService.getCurrentNavigation().pipe(filter((val) => val.questionnairesNavigation != null))
    ])
      .pipe(
        map(([routerEvent, navigationObject]) => {
          if (
            JSON.stringify(routerEvent) === JSON.stringify(this.lastRouterEvent) ||
            (JSON.stringify(navigationObject) === JSON.stringify(this.lastNavigationObject) &&
              (routerEvent as NavigationEnd).urlAfterRedirects != '/')
          ) {
            return null; // Si se repite el último estado, emitir null
          } else {
            this.lastRouterEvent = routerEvent;
            this.lastNavigationObject = navigationObject;
            return [routerEvent, navigationObject];
          }
        }),
        filter((value) => value !== null), // Filtrar los valores null
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(([_, navigationObject]) => {
        this.router.navigateByUrl((navigationObject as NavigationStateImports).questionnairesNavigation.children[0].children[0].route);
      });

    // NOTIS

    timer(0, 300000)
      .pipe(switchMap((_) => this.userFacade.isLoggedIn$))
      .subscribe({
        next: (val) => {
          if (val) {
            this.notificationFacade.getNotifications();
          }
        }
      });
  }
}
